/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import moment from 'moment';
import {error, warn} from '../logger/logger';
import {fromUnixTimeStamp} from './DateUtil';
import {safeJSONParse} from './JSONUtils';

/**
 * Defines the `parseJWT` result return type which contains the provided token,
 * expiration Date, and issuance Date.
 */
export type TokenAuthorization = {
  expiration: Date,
  issuance: Date,
  token: string
};

/**
 * Provides a utility which parses a JWT (JSON Web Token) and returns an object which
 * contains the token, issuance (iat) and expiration (exp) claims. If the provided JWT
 * does not contain the required claims (iat and / or exp), the issuance claim will be
 * provided a default of the current time, and the expiration will be provided a default
 * of the 24 hours from the issuance.
 *
 * ```typescript
 *   import {parseJWT} from '@ion/core/src/utils/JSONUtils';
 *
 *   const {expiration, issuance, token} = parseJWT(''eyJraWQiOiJ...');
 * ```
 *
 * @param   token The JWT which is to be decoded and have the given token and claims
 *          returned.
 * @returns An object containing the token, issuance (iat) and expiration (exp) claims.
 *          If the token is valid, but does not contain the required claims, a warning
 *          will be logged, and the issuance date will default to the current time,
 *          with the expiration defaulting to 24hrs from the current time. If the token
 *          is invalid and can not be parsed, an error will be logged and undefined will
 *          be returned.
 */
export const parseJWT = (token: string): TokenAuthorization | undefined => {
  try {
    const url    = token.split('.')?.[1];
    const base64 = url.replace(/-/g, '+').replace(/_/g, '/');
    const json   = decodeURIComponent(atob(base64).split('').map(char => (
      '%' + ('00' + char.charCodeAt(0).toString(16)
      ).slice(-2)
    )).join(''));
    const {exp, iat} = safeJSONParse(json);
    if (exp && iat) {
      return {
        expiration : fromUnixTimeStamp(exp),
        issuance   : fromUnixTimeStamp(iat),
        token
      };
    } else {
      warn(`Expiration and / or Issuance claims not included in Token, providing defaults for ${token}`);
      return {
        ...createFallbacks(),
        token
      };
    }
  } catch (err) {
    error(`An Error ocurred while parsing the provided JWT ${token}`);
  }
  if (token.match(/^[a-z0-9]+$/gi)) {
    return {
      ...createFallbacks(),
      token
    };
  }
};

/**
 * Provides a utility which generates a fallback issuance and expiration for tokens which
 * do not contain them. This is generally the case for CTC short tokens whereby they do
 * not implement the JWT specification.
 *
 * @returns An object containing an `issuance` based on the current time, and `expiration`
 *          24 hours from the time of `issuance`.
 */
export const createFallbacks = () => {
  const issuance = moment();
  return {
    expiration : fromUnixTimeStamp(issuance.clone().add(24, 'hours').unix()),
    issuance   : fromUnixTimeStamp(issuance.unix())
  };
};
